// =============================================================================
// Fulfillment Settings — per-store provider/method configuration (Phase 2)
// =============================================================================
// Portal twin of the embedded app.fulfillment.tsx page. Lets the merchant
// choose, per product, WHO fulfills (Transfer Kingdom / Self-fulfill) and HOW
// TK fulfillment is triggered (Auto / Manual).
//   • Custom products (storefront-designed): provider + method selectable.
//   • My products (POD): provider locked to Transfer Kingdom, method only.
// Group defaults live on the store connection; per-product overrides win.
// Switching to Self-fulfill requires an explicit confirmation — it removes
// the product from the TK shipping profile/location and those orders are
// never charged. Writes need settings:manage; reads need settings:read.
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@tkpod/api-client';
import {
  Badge,
  Button,
  Card,
  Input,
  Select,
  Spinner,
  useConfirm,
} from '@tkpod/ui';
import {
  FULFILLMENT_METHOD_LABELS,
  FULFILLMENT_PROVIDER_LABELS,
  SELF_FULFILL_CONFIRMATION_MESSAGE,
  hasAnyPermission,
  type EffectiveFulfillmentSettings,
  type FulfillmentGroupDefaults,
  type FulfillmentLane,
  type FulfillmentMethod,
  type FulfillmentProvider,
} from '@tkpod/shared';

import { useAuth } from '@/hooks/use-auth';
import { useConnections } from '@/hooks/use-connections';

// ---------------------------------------------------------------------------
// Wire types — mirror apps/api/src/modules/fulfillment-settings responses
// ---------------------------------------------------------------------------

interface ProductRow {
  id: string;
  title: string;
  previewImageUrl: string | null;
  status: string;
  lane: FulfillmentLane;
  effective: EffectiveFulfillmentSettings;
  overrides: {
    provider: FulfillmentProvider | null;
    method: FulfillmentMethod | null;
  };
  sync: { status: string; error: string | null; syncedAt: string | null };
}

interface SettingsResponse {
  connection: { id: string; name: string; shopDomain: string | null };
  defaults: FulfillmentGroupDefaults;
  defaultUsage: {
    customOnDefaultProvider: number;
    customOnDefaultMethod: number;
    podOnDefaultMethod: number;
  };
  counts: { all: number; custom: number; pod: number };
  data: ProductRow[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

/** `INHERIT` clears the override so the product follows the group default. */
interface OverrideWrite {
  provider?: 'TK_POD' | 'SELF' | 'INHERIT';
  method?: 'AUTO' | 'MANUAL' | 'INHERIT';
}

type LaneFilter = 'ALL' | 'CUSTOM' | 'POD';

const PAGE_SIZE = 25;

export function FulfillmentSettingsPage() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { confirm, dialog } = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();

  const permissions = user?.currentOrganization?.permissions ?? [];
  const canManage = hasAnyPermission(permissions, ['settings:manage']);

  // ---- store selection ------------------------------------------------------
  const connectionsQuery = useConnections();
  const shopifyConnections = useMemo(
    () => (connectionsQuery.data ?? []).filter((c) => c.type === 'SHOPIFY'),
    [connectionsQuery.data],
  );

  const urlConnectionId = searchParams.get('connectionId') ?? '';
  // Settings are per-store, so a store must always be selected — default to
  // the first active Shopify connection (or any, if none is active).
  const connectionId = useMemo(() => {
    if (
      urlConnectionId &&
      shopifyConnections.some((c) => c.id === urlConnectionId)
    ) {
      return urlConnectionId;
    }
    const active = shopifyConnections.find((c) => c.status === 'ACTIVE');
    return active?.id ?? shopifyConnections[0]?.id ?? '';
  }, [urlConnectionId, shopifyConnections]);

  // ---- URL-backed filters ---------------------------------------------------
  const lane = (searchParams.get('lane') ?? 'ALL') as LaneFilter;
  const providerFilter = searchParams.get('provider') ?? 'ALL';
  const search = searchParams.get('search') ?? '';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => setSearchInput(search), [search]);

  const setParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(searchParams);
      if (!value || value === 'ALL') next.delete(key);
      else next.set(key, value);
      if (key !== 'page') next.delete('page');
      setSearchParams(next);
    },
    [searchParams, setSearchParams],
  );

  // ---- selection (bulk) -----------------------------------------------------
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkProvider, setBulkProvider] = useState('');
  const [bulkMethod, setBulkMethod] = useState('');
  useEffect(() => {
    setSelectedIds(new Set());
    setBulkProvider('');
    setBulkMethod('');
  }, [lane, providerFilter, search, page, connectionId]);

  // ---- data -----------------------------------------------------------------
  const queryKey = [
    'portal',
    'fulfillment-settings',
    connectionId,
    { lane, providerFilter, search, page },
  ];
  const settingsQuery = useQuery({
    queryKey,
    enabled: connectionId.length > 0,
    queryFn: () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(PAGE_SIZE));
      if (lane !== 'ALL') params.set('lane', lane);
      if (providerFilter !== 'ALL') params.set('provider', providerFilter);
      if (search) params.set('search', search);
      return api.get<SettingsResponse>(
        `/api/v1/fulfillment-settings/${connectionId}?${params.toString()}`,
      );
    },
  });

  const invalidate = useCallback(
    () =>
      void queryClient.invalidateQueries({
        queryKey: ['portal', 'fulfillment-settings', connectionId],
      }),
    [queryClient, connectionId],
  );

  // ---- mutations -------------------------------------------------------------
  const [feedback, setFeedback] = useState<{
    tone: 'success' | 'error';
    text: string;
  } | null>(null);
  useEffect(() => {
    if (!feedback) return;
    const timer = setTimeout(() => setFeedback(null), 6000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const onMutationError = (error: unknown) =>
    setFeedback({
      tone: 'error',
      text: error instanceof Error ? error.message : 'Update failed',
    });

  const defaultsMutation = useMutation({
    mutationFn: (body: Record<string, string>) =>
      api.patch<{ defaults: FulfillmentGroupDefaults; resyncedProducts: number }>(
        `/api/v1/fulfillment-settings/${connectionId}/defaults`,
        body,
      ),
    onSuccess: (result) => {
      invalidate();
      setFeedback({
        tone: 'success',
        text:
          result.resyncedProducts > 0
            ? `Defaults saved. ${result.resyncedProducts} product(s) are re-syncing with Shopify.`
            : 'Defaults saved.',
      });
    },
    onError: onMutationError,
  });

  const productMutation = useMutation({
    mutationFn: ({ productId, write }: { productId: string; write: OverrideWrite }) =>
      api.patch<{ data: ProductRow }>(
        `/api/v1/fulfillment-settings/${connectionId}/products/${productId}`,
        write,
      ),
    onSuccess: () => {
      invalidate();
      setFeedback({ tone: 'success', text: 'Product updated.' });
    },
    onError: onMutationError,
  });

  const bulkMutation = useMutation({
    mutationFn: (body: OverrideWrite & { productIds: string[] }) =>
      api.post<{ updated: number; syncEnqueued: number }>(
        `/api/v1/fulfillment-settings/${connectionId}/products/bulk`,
        body,
      ),
    onSuccess: (result) => {
      invalidate();
      setSelectedIds(new Set());
      setFeedback({
        tone: 'success',
        text: `${result.updated} product(s) updated.`,
      });
    },
    onError: onMutationError,
  });

  const isMutating =
    defaultsMutation.isPending ||
    productMutation.isPending ||
    bulkMutation.isPending;

  // ---- handlers (SELF needs confirmation) ------------------------------------

  const confirmSelfSwitch = useCallback(
    (description: string) =>
      confirm({
        title: 'Switch to Self-fulfill',
        description,
        confirmLabel: 'Switch to Self-fulfill',
        tone: 'danger',
      }),
    [confirm],
  );

  const handleRowProviderChange = useCallback(
    async (row: ProductRow, value: string) => {
      if (value === 'SELF') {
        const ok = await confirmSelfSwitch(SELF_FULFILL_CONFIRMATION_MESSAGE);
        if (!ok) return;
      }
      if (value === 'SELF' || value === 'TK_POD' || value === 'INHERIT') {
        productMutation.mutate({ productId: row.id, write: { provider: value } });
      }
    },
    [confirmSelfSwitch, productMutation],
  );

  const handleRowMethodChange = useCallback(
    (row: ProductRow, value: string) => {
      if (value === 'AUTO' || value === 'MANUAL' || value === 'INHERIT') {
        productMutation.mutate({ productId: row.id, write: { method: value } });
      }
    },
    [productMutation],
  );

  const handleDefaultProviderChange = useCallback(
    async (value: string) => {
      const defaults = settingsQuery.data?.defaults;
      if (!defaults || value === defaults.customDefaultProvider) return;
      if (value === 'SELF') {
        const ok = await confirmSelfSwitch(
          `This sets the DEFAULT for all custom products without an override. ${SELF_FULFILL_CONFIRMATION_MESSAGE}`,
        );
        if (!ok) return;
      }
      defaultsMutation.mutate({ customDefaultProvider: value });
    },
    [settingsQuery.data?.defaults, confirmSelfSwitch, defaultsMutation],
  );

  const handleBulkApply = useCallback(async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    const write: OverrideWrite = {};
    if (
      bulkProvider === 'TK_POD' ||
      bulkProvider === 'SELF' ||
      bulkProvider === 'INHERIT'
    ) {
      write.provider = bulkProvider;
    }
    if (
      bulkMethod === 'AUTO' ||
      bulkMethod === 'MANUAL' ||
      bulkMethod === 'INHERIT'
    ) {
      write.method = bulkMethod;
    }
    if (!write.provider && !write.method) return;
    if (write.provider === 'SELF') {
      const ok = await confirmSelfSwitch(
        `${ids.length} product(s) will switch to Self-fulfill. ${SELF_FULFILL_CONFIRMATION_MESSAGE}`,
      );
      if (!ok) return;
    }
    bulkMutation.mutate({ ...write, productIds: ids });
  }, [selectedIds, bulkProvider, bulkMethod, confirmSelfSwitch, bulkMutation]);

  // ---- selection helpers ------------------------------------------------------
  const rows = settingsQuery.data?.data ?? [];
  const bulkEnabled = canManage && (lane === 'CUSTOM' || lane === 'POD');
  const allVisibleSelected =
    rows.length > 0 && rows.every((r) => selectedIds.has(r.id));

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (rows.length > 0 && rows.every((r) => prev.has(r.id))) {
        for (const r of rows) next.delete(r.id);
      } else {
        for (const r of rows) next.add(r.id);
      }
      return next;
    });
  }, [rows]);

  // ---- render -----------------------------------------------------------------

  if (connectionsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (shopifyConnections.length === 0) {
    return (
      <Card>
        <h2 className="text-lg font-semibold text-text-primary">Fulfillment</h2>
        <p className="mt-2 text-sm text-text-secondary">
          Connect a Shopify store to configure fulfillment settings.
        </p>
      </Card>
    );
  }

  const settings = settingsQuery.data;
  const defaults = settings?.defaults;

  return (
    <div className="space-y-6">
      {dialog}

      {/* Page header + store picker */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">
            Fulfillment
          </h1>
          <p className="mt-0.5 text-sm text-text-tertiary">
            Choose who fulfills each product and how Transfer Kingdom
            fulfillment is triggered. Changes only affect new orders.
          </p>
        </div>
        <div className="w-full sm:w-[260px]">
          <Select
            label="Store"
            value={connectionId}
            onChange={(e) => setParam('connectionId', e.target.value)}
            options={shopifyConnections.map((c) => ({
              value: c.id,
              label: c.status === 'ACTIVE' ? c.name : `${c.name} (inactive)`,
            }))}
            aria-label="Select store"
          />
        </div>
      </div>

      {/* Payment / behavior explainer */}
      <div className="rounded-lg border border-border bg-info/10 px-4 py-3 text-sm text-text-secondary">
        <p>
          Orders for products <strong>fulfilled by Transfer Kingdom</strong>{' '}
          are charged the wholesale production + shipping cost.{' '}
          <strong>Self-fulfilled</strong> products are never charged — you
          print and ship them yourself, and they are removed from the Transfer
          Kingdom shipping profile and location.
        </p>
        <p className="mt-1">
          Method controls when production starts: <strong>Auto</strong> queues
          orders immediately; <strong>Manual</strong> waits until you click
          &quot;Request fulfillment&quot; on the order in your Shopify admin.
        </p>
      </div>

      {feedback && (
        <div
          className={
            feedback.tone === 'success'
              ? 'rounded-md bg-success/10 px-4 py-3 text-sm text-success'
              : 'rounded-md bg-danger/10 px-4 py-3 text-sm text-danger'
          }
          role="status"
        >
          {feedback.text}
        </div>
      )}

      {!canManage && (
        <div className="rounded-lg border border-border bg-surface-secondary/40 px-4 py-3 text-sm text-text-tertiary">
          You have read-only access — changing fulfillment settings requires
          the settings:manage permission.
        </div>
      )}

      {settingsQuery.isLoading || !settings || !defaults ? (
        <div className="flex items-center justify-center py-20">
          {settingsQuery.isError ? (
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-text-secondary">
                Failed to load fulfillment settings.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => settingsQuery.refetch()}
              >
                Retry
              </Button>
            </div>
          ) : (
            <Spinner size="lg" />
          )}
        </div>
      ) : (
        <>
          {/* Default cards */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <h3 className="text-sm font-semibold text-text-primary">
                Custom products
              </h3>
              <p className="mt-0.5 text-xs text-text-tertiary">
                Storefront products your customers personalize.
              </p>
              <div className="mt-4 space-y-3">
                <Select
                  label="Default fulfillment provider"
                  value={defaults.customDefaultProvider}
                  disabled={!canManage || isMutating}
                  onChange={(e) =>
                    void handleDefaultProviderChange(e.target.value)
                  }
                  options={[
                    { value: 'TK_POD', label: 'Fulfilled by Transfer Kingdom' },
                    { value: 'SELF', label: 'Self-fulfill' },
                  ]}
                />
                <Select
                  label="Default method"
                  value={defaults.customDefaultMethod}
                  disabled={
                    !canManage ||
                    isMutating ||
                    defaults.customDefaultProvider === 'SELF'
                  }
                  onChange={(e) =>
                    defaultsMutation.mutate({
                      customDefaultMethod: e.target.value,
                    })
                  }
                  options={[
                    { value: 'AUTO', label: 'Auto' },
                    { value: 'MANUAL', label: 'Manual' },
                  ]}
                />
                <p className="text-xs text-text-tertiary">
                  Applies to {settings.defaultUsage.customOnDefaultProvider}{' '}
                  product
                  {settings.defaultUsage.customOnDefaultProvider === 1
                    ? ''
                    : 's'}{' '}
                  using the default.
                </p>
                <p className="text-xs text-text-tertiary">
                  {defaults.customDefaultProvider === 'SELF'
                    ? 'Self-fulfilled products are never charged — you print and ship them yourself.'
                    : 'Orders fulfilled by Transfer Kingdom are charged the wholesale production + shipping cost.'}
                </p>
              </div>
            </Card>

            <Card>
              <h3 className="text-sm font-semibold text-text-primary">
                My products
              </h3>
              <p className="mt-0.5 text-xs text-text-tertiary">
                Products designed by you — always fulfilled by Transfer
                Kingdom.
              </p>
              <div className="mt-4 space-y-3">
                <div>
                  <p className="mb-1 block text-sm font-medium text-text-primary">
                    Fulfillment provider
                  </p>
                  <div className="flex items-center gap-2 rounded-md border border-border bg-surface-secondary/40 px-3 py-2 text-sm text-text-secondary">
                    <LockIcon className="h-4 w-4 shrink-0" />
                    Fulfilled by Transfer Kingdom
                  </div>
                </div>
                <Select
                  label="Default method"
                  value={defaults.podDefaultMethod}
                  disabled={!canManage || isMutating}
                  onChange={(e) =>
                    defaultsMutation.mutate({ podDefaultMethod: e.target.value })
                  }
                  options={[
                    { value: 'AUTO', label: 'Auto' },
                    { value: 'MANUAL', label: 'Manual' },
                  ]}
                />
                <p className="text-xs text-text-tertiary">
                  Applies to {settings.defaultUsage.podOnDefaultMethod} product
                  {settings.defaultUsage.podOnDefaultMethod === 1 ? '' : 's'}{' '}
                  using the default.
                </p>
                <p className="text-xs text-text-tertiary">
                  Orders fulfilled by Transfer Kingdom are charged the
                  wholesale production + shipping cost.
                </p>
              </div>
            </Card>
          </div>

          {/* Product list */}
          <Card padding="none">
            {/* Tabs + filters */}
            <div className="flex flex-col gap-4 border-b border-border px-5 py-4">
              <div className="flex flex-wrap items-center gap-2">
                {(
                  [
                    { key: 'ALL', label: `All (${settings.counts.all})` },
                    {
                      key: 'CUSTOM',
                      label: `Custom products (${settings.counts.custom})`,
                    },
                    {
                      key: 'POD',
                      label: `My products (${settings.counts.pod})`,
                    },
                  ] as const
                ).map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setParam('lane', tab.key)}
                    className={
                      lane === tab.key
                        ? 'rounded-md bg-brand-50 px-3 py-1.5 text-sm font-medium text-brand-700'
                        : 'rounded-md px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-secondary'
                    }
                  >
                    {tab.label}
                  </button>
                ))}
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  <div className="min-w-[200px]">
                    <Input
                      placeholder="Search by title..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={(e) =>
                        e.key === 'Enter' &&
                        setParam('search', searchInput.trim())
                      }
                      aria-label="Search products"
                    />
                  </div>
                  <div className="w-[170px]">
                    <Select
                      value={providerFilter}
                      onChange={(e) => setParam('provider', e.target.value)}
                      options={[
                        { value: 'ALL', label: 'All providers' },
                        { value: 'TK_POD', label: 'Transfer Kingdom' },
                        { value: 'SELF', label: 'Self-fulfill' },
                      ]}
                      aria-label="Filter by provider"
                    />
                  </div>
                </div>
              </div>

              {/* Bulk bar — lane tabs only */}
              {bulkEnabled && selectedIds.size > 0 && (
                <div className="flex flex-wrap items-end gap-3 rounded-md border border-border bg-surface-secondary/40 px-4 py-3">
                  <span className="text-sm font-medium text-text-primary">
                    {selectedIds.size} selected
                  </span>
                  {lane === 'CUSTOM' && (
                    <div className="w-[180px]">
                      <Select
                        label="Set provider"
                        value={bulkProvider}
                        onChange={(e) => setBulkProvider(e.target.value)}
                        options={[
                          { value: '', label: 'Keep current' },
                          { value: 'TK_POD', label: 'Transfer Kingdom' },
                          { value: 'SELF', label: 'Self-fulfill' },
                          { value: 'INHERIT', label: 'Use default' },
                        ]}
                      />
                    </div>
                  )}
                  <div className="w-[160px]">
                    <Select
                      label="Set method"
                      value={bulkMethod}
                      onChange={(e) => setBulkMethod(e.target.value)}
                      options={[
                        { value: '', label: 'Keep current' },
                        { value: 'AUTO', label: 'Auto' },
                        { value: 'MANUAL', label: 'Manual' },
                        { value: 'INHERIT', label: 'Use default' },
                      ]}
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => void handleBulkApply()}
                    disabled={isMutating || (!bulkProvider && !bulkMethod)}
                    loading={bulkMutation.isPending}
                  >
                    Apply
                  </Button>
                </div>
              )}
            </div>

            {/* Table */}
            {rows.length === 0 ? (
              <div className="py-20 text-center">
                <p className="text-sm text-text-secondary">No products found.</p>
                <p className="mt-1 text-xs text-text-tertiary">
                  Try adjusting your filters.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-surface-secondary/40">
                      <th className="w-10 px-5 py-2.5 text-left">
                        {bulkEnabled && (
                          <input
                            type="checkbox"
                            checked={allVisibleSelected}
                            onChange={toggleAllVisible}
                            aria-label="Select all visible products"
                          />
                        )}
                      </th>
                      <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                        Product
                      </th>
                      <th className="hidden px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary md:table-cell">
                        Type
                      </th>
                      <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                        Fulfillment provider
                      </th>
                      <th className="px-5 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-text-tertiary">
                        Method
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        className="transition-colors hover:bg-surface-secondary/40"
                      >
                        <td className="px-5 py-2.5">
                          {bulkEnabled && (
                            <input
                              type="checkbox"
                              checked={selectedIds.has(row.id)}
                              onChange={() => toggleRow(row.id)}
                              aria-label={`Select ${row.title}`}
                            />
                          )}
                        </td>
                        <td className="px-5 py-2.5">
                          <div className="flex min-w-0 items-center gap-3">
                            {row.previewImageUrl ? (
                              <img
                                src={row.previewImageUrl}
                                alt=""
                                loading="lazy"
                                className="h-10 w-10 shrink-0 rounded border border-border bg-surface-secondary object-cover"
                              />
                            ) : (
                              <div className="h-10 w-10 shrink-0 rounded border border-border bg-surface-secondary" />
                            )}
                            <div className="min-w-0">
                              <p className="truncate font-medium text-text-primary">
                                {row.title}
                              </p>
                              <div className="mt-0.5 flex flex-wrap items-center gap-1">
                                {row.effective.provider === 'SELF' && (
                                  <Badge variant="warning" size="sm">
                                    Self-fulfilled
                                  </Badge>
                                )}
                                {row.effective.method === 'MANUAL' && (
                                  <Badge variant="info" size="sm">
                                    Manual
                                  </Badge>
                                )}
                                {(row.effective.providerSource === 'OVERRIDE' ||
                                  row.effective.methodSource ===
                                    'OVERRIDE') && (
                                  <Badge variant="default" size="sm">
                                    Override
                                  </Badge>
                                )}
                                {row.sync.status === 'PENDING' && (
                                  <Badge variant="warning" size="sm" dot>
                                    Sync pending
                                  </Badge>
                                )}
                                {row.sync.status === 'ERROR' && (
                                  <Badge
                                    variant="danger"
                                    size="sm"
                                    title={row.sync.error ?? undefined}
                                  >
                                    Sync error
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="hidden px-5 py-2.5 md:table-cell">
                          {row.lane === 'CUSTOM' ? (
                            <Badge variant="info" size="sm">
                              Custom
                            </Badge>
                          ) : (
                            <Badge variant="default" size="sm">
                              My product
                            </Badge>
                          )}
                        </td>
                        <td className="px-5 py-2.5">
                          {row.lane === 'CUSTOM' ? (
                            <div className="w-[210px]">
                              <Select
                                value={row.overrides.provider ?? 'INHERIT'}
                                disabled={!canManage || isMutating}
                                onChange={(e) =>
                                  void handleRowProviderChange(
                                    row,
                                    e.target.value,
                                  )
                                }
                                options={[
                                  {
                                    value: 'INHERIT',
                                    label: `Default (${FULFILLMENT_PROVIDER_LABELS[defaults.customDefaultProvider]})`,
                                  },
                                  { value: 'TK_POD', label: 'Transfer Kingdom' },
                                  { value: 'SELF', label: 'Self-fulfill' },
                                ]}
                                aria-label={`Fulfillment provider for ${row.title}`}
                              />
                            </div>
                          ) : (
                            <span className="flex items-center gap-1.5 text-text-secondary">
                              <LockIcon className="h-3.5 w-3.5 shrink-0" />
                              Transfer Kingdom
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-2.5">
                          {row.effective.provider === 'SELF' ? (
                            <span className="text-text-tertiary">—</span>
                          ) : (
                            <div className="w-[170px]">
                              <Select
                                value={row.overrides.method ?? 'INHERIT'}
                                disabled={!canManage || isMutating}
                                onChange={(e) =>
                                  handleRowMethodChange(row, e.target.value)
                                }
                                options={[
                                  {
                                    value: 'INHERIT',
                                    label: `Default (${
                                      FULFILLMENT_METHOD_LABELS[
                                        row.lane === 'CUSTOM'
                                          ? defaults.customDefaultMethod
                                          : defaults.podDefaultMethod
                                      ]
                                    })`,
                                  },
                                  { value: 'AUTO', label: 'Auto' },
                                  { value: 'MANUAL', label: 'Manual' },
                                ]}
                                aria-label={`Fulfillment method for ${row.title}`}
                              />
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {settings.pagination.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-border px-5 py-3">
                <p className="text-xs text-text-tertiary">
                  Page {settings.pagination.page} of{' '}
                  {settings.pagination.totalPages} —{' '}
                  {settings.pagination.total} product
                  {settings.pagination.total === 1 ? '' : 's'}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={settings.pagination.page <= 1}
                    onClick={() =>
                      setParam('page', String(settings.pagination.page - 1))
                    }
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      settings.pagination.page >= settings.pagination.totalPages
                    }
                    onClick={() =>
                      setParam('page', String(settings.pagination.page + 1))
                    }
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
      />
    </svg>
  );
}
